import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Eye, Tag } from 'lucide-react';
import axios from 'axios';

const gold = '#C9A961';

export default function BlogList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit: 12 });
        if (category) params.set('category', category);
        const [postsRes, catsRes] = await Promise.all([
          axios.get(`/api/blog/posts?${params}`),
          axios.get('/api/blog/categories'),
        ]);
        setPosts(postsRes.data.posts || []);
        setTotal(postsRes.data.total || 0);
        setPages(postsRes.data.pages || 1);
        setCategories(catsRes.data.categories || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [page, category]);

  return (
    <div className="min-h-screen" style={{ background: '#080D26', fontFamily: 'Manrope, sans-serif' }}>
      {/* SEO meta tags injected via Helmet or document.title */}
      <header className="border-b backdrop-blur-xl sticky top-0 z-50" style={{ background: 'rgba(8,13,38,0.92)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-white text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            <img src="/logo.png" alt="Astrowins" className="h-10 w-auto" />
            ASTROWINS
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm font-medium" style={{ color: gold }}>Blog</Link>
            <Link to="/auth" className="text-slate-300 hover:text-white text-sm px-3 py-2">Login</Link>
            <Link to="/checkout" className="px-4 py-2 rounded-lg font-semibold text-sm" style={{ background: gold, color: '#080D26' }}>Get Started</Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Astrowins <span style={{ color: gold }}>Blog</span>
          </h1>
          <p className="text-slate-400 text-base mb-8">Insights on Numerology, Astrology, Vastu & more</p>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-categories">
            <Link to="/blog" className={`px-3 py-1.5 rounded-full text-sm font-medium ${!category ? 'text-black' : 'text-slate-400 border border-slate-700'}`}
              style={!category ? { background: gold } : {}}>All</Link>
            {categories.map(c => (
              <Link key={c.name} to={`/blog?category=${c.name}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${category === c.name ? 'text-black' : 'text-slate-400 border border-slate-700'}`}
                style={category === c.name ? { background: gold } : {}}>
                {c.name} ({c.count})
              </Link>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20"><div className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${gold} transparent ${gold} ${gold}` }} /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {posts.map(post => (
                  <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
                    className="group rounded-2xl overflow-hidden transition-all hover:scale-[1.02]"
                    style={{ background: '#101632', border: '1px solid rgba(255,255,255,0.06)' }}>
                    {post.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5">
                      {post.category && (
                        <span className="text-xs font-semibold uppercase tracking-wider mb-2 inline-block" style={{ color: gold }}>{post.category}</span>
                      )}
                      <h2 className="text-white font-bold text-lg mb-2 line-clamp-2 group-hover:text-amber-200 transition-colors">{post.title}</h2>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                        <span className="flex items-center gap-1"><Eye size={12} /> {post.views || 0}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  {page > 1 && <Link to={`/blog?page=${page - 1}${category ? `&category=${category}` : ''}`} className="px-4 py-2 rounded-lg text-sm border border-slate-700 text-slate-400">Previous</Link>}
                  {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map(p => (
                    <Link key={p} to={`/blog?page=${p}${category ? `&category=${category}` : ''}`}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${p === page ? 'text-black' : 'text-slate-400 border border-slate-700'}`}
                      style={p === page ? { background: gold } : {}}>
                      {p}
                    </Link>
                  ))}
                  {page < pages && <Link to={`/blog?page=${page + 1}${category ? `&category=${category}` : ''}`} className="px-4 py-2 rounded-lg text-sm border border-slate-700 text-slate-400">Next</Link>}
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
